/**
 * "Popular Products to Stack" — in-buy-box upsell on the PDP (WIL-29).
 *
 * Fetches complementary recommendations for the current product from the
 * Recommendations JSON API (falls back to related when no complementary
 * products are curated in Search & Discovery), renders a compact list, and adds
 * a pick straight to the cart — reusing the same cart:refresh flow as the rest
 * of the theme so the drawer opens on add. Hides itself when nothing is returned.
 */
if (!customElements.get('ws-product-stack')) {
  const ROOT = () => window.Shopify?.routes?.root ?? '/';

  class WsProductStack extends HTMLElement {
    #abort = null;

    connectedCallback() {
      this.#abort = new AbortController();
      this.#load();
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    async #load() {
      const productId = this.dataset.productId;
      const list = this.querySelector('[data-stack-list]');
      if (!productId || !list) return;

      const limit = Number(this.dataset.limit || 3);
      const picks =
        (await this.#fetch(productId, 'complementary', limit)) ||
        (await this.#fetch(productId, 'related', limit)) ||
        [];

      if (picks.length === 0) {
        this.hidden = true;
        return;
      }

      list.replaceChildren();
      for (const product of picks) list.append(this.#buildItem(product, list.dataset.textAdd));
      this.hidden = false;
    }

    /** Returns an array of available picks, or null on error/empty. */
    async #fetch(productId, intent, limit) {
      try {
        const url = new URL(`${ROOT()}recommendations/products.json`, window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', String(limit * 2));
        url.searchParams.set('intent', intent);

        const response = await fetch(url, { signal: this.#abort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const { products = [] } = await response.json();
        const picks = products.filter((p) => p.available && String(p.id) !== String(productId)).slice(0, limit);
        return picks.length ? picks : null;
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Stack recommendations failed:', error);
        return null;
      }
    }

    #money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active ?? 'USD'
      }).format(cents / 100);
    }

    #buildItem(product, addLabel) {
      const item = document.createElement('div');
      item.className = 'ws-product-stack__item';

      const thumb = document.createElement('a');
      thumb.className = 'ws-product-stack__thumb';
      thumb.href = product.url;
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image;
        image.alt = '';
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        thumb.append(image);
      }

      const info = document.createElement('div');
      info.className = 'ws-product-stack__info';

      // Product vendor is the store name post-migration (not the brand), so it's
      // omitted here — the brand already reads in the product title.
      const title = document.createElement('a');
      title.className = 'ws-product-stack__title';
      title.href = product.url;
      title.textContent = product.title;
      info.append(title);

      const price = document.createElement('p');
      price.className = 'ws-product-stack__price';
      const onSale = product.compare_at_price && product.compare_at_price > product.price;
      const current = document.createElement('span');
      current.className = 'ws-product-stack__price-current';
      if (onSale) current.classList.add('ws-product-stack__price-current--sale');
      current.textContent = this.#money(product.price);
      price.append(current);
      if (onSale) {
        const compare = document.createElement('s');
        compare.className = 'ws-product-stack__price-compare';
        compare.textContent = this.#money(product.compare_at_price);
        price.append(compare);
      }
      info.append(price);

      const button = document.createElement('button');
      button.className = 'ws-product-stack__add';
      button.type = 'button';
      button.textContent = addLabel || 'Add';
      const variantId = product.variants?.find((v) => v.available)?.id ?? product.variants?.[0]?.id ?? product.id;
      button.addEventListener(
        'click',
        async () => {
          button.setAttribute('aria-busy', 'true');
          try {
            const response = await fetch(`${ROOT()}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          } catch (error) {
            console.error('Stack add failed:', error);
          } finally {
            button.removeAttribute('aria-busy');
          }
        },
        { signal: this.#abort.signal }
      );

      item.append(thumb, info, button);
      return item;
    }
  }

  customElements.define('ws-product-stack', WsProductStack);
}
